import { useEffect } from "react"
import { computed, reaction } from "mobx"
import { observer } from "mobx-react"
import {
  Alert,
  Button,
  Col,
  Flex,
  Form,
  FormInstance,
  Input,
  Row,
  Space,
} from "antd"

import { balancesStore } from "@src/app/balanceStore"
import { ConvertProcess } from "@src/pages/convert/classes/ConvertProcess"
import { debounce } from "@src/pages/convert/debounce"
import { getBalance } from "@src/pages/convert/getOptions"
import { ConvertFormAvailableBalance } from "@src/pages/convert/ui/ConvertFormAvailableBalance"
import { ConvertFormRates } from "@src/pages/convert/ui/ConvertFormRates"
import { CurrencySelectField } from "@src/shared/componemts/CurrencySelectField"
import { mergeArrays } from "@src/shared/mergeArrays"
import css from "../index.module.css"

type Props = {
  form: FormInstance
  mode: "from" | "to"
  process: ConvertProcess
  submit: (v: any) => Promise<any>
}
export const ConvertForm = observer(
  ({ form, mode, process, submit }: Props) => {
    const { balances, currencies } = balancesStore

    const [balance, inputPattern] = computed(() => {
      const from = process.currencyFrom
      if (from) {
        const { balance, decimals } = getBalance(
          mergeArrays(balances, currencies),
          from,
        )

        return [
          balance,
          new RegExp(
            `^(?!0{2,})\\d+(\\.\\d{1,${process.currencyFrom.network === "fiat" ? 2 : decimals}})?$`,
          ),
        ] as [string, RegExp]
      }

      return []
    }).get()

    const validateAmount = async (v: string) => {
      if (!v) {
        return Promise.reject(`Please input amount`)
      }

      if (Number(v) <= 0) {
        return Promise.reject(`Please input amount`)
      }

      if (Number(v) > Number(balance)) {
        return Promise.reject(`Maximum withdrawal amount: ${balance}`)
      }

      if (!v.match(inputPattern)) {
        return Promise.reject(new Error("Invalid number of decimal places"))
      }

      return Promise.resolve()
    }

    const debouncedUpdateAmount = debounce(
      (e: string) => process.setAmount(e),
      1000,
    )

    useEffect(() => {
      const killCurrencyFromReaction = reaction(
        () => (mode === "from" ? process.currencyFrom : process.currencyTo),
        (v, o) => {
          const isChanged = v?.id !== o?.id
          if (isChanged) {
            form.resetFields([
              "amount",
              mode === "from" ? "currencyTo" : "currencyFrom",
            ])
            process.resetState()
          }
        },
      )

      const killAmountReaction = reaction(
        () => [
          process.currencyFrom?.id,
          process.currencyTo?.id,
          process.state.amount,
        ],
        (v) => {
          const hasErrors = form.getFieldError("amount").length > 0
          const isInvalidRequest = v.some((value) => !value)

          if (isInvalidRequest || hasErrors) {
            return void 0
          }

          return process.fetchRate()
        },
      )

      return () => {
        killAmountReaction()
        killCurrencyFromReaction()
      }
    }, [])
    return (
      <Form
        className={css.form}
        form={form}
        id="GENERATED_ID_CONVERTFORM_FORM_VOID_1"
        layout="vertical"
        style={{ margin: "0 auto", width: "50%" }}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Row gutter={16}>
              <Col span={16}>
                <Form.Item
                  dependencies={["currencyTo", "currencyFrom"]}
                  extra={<ConvertFormAvailableBalance balance={balance} />}
                  id="GENERATED_ID_CONVERTFORM_TSX_FORM.ITEM_VOID_1"
                  label="You spend"
                  name="amount"
                  rules={[
                    {
                      required: true,
                      validator: (_, value) => validateAmount(value),
                    },
                  ]}
                >
                  <Input
                    className={css.field}
                    disabled={process.isLoading}
                    onChange={(e) => debouncedUpdateAmount(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        e.stopPropagation()
                      }
                    }}
                    placeholder="0.00"
                    suffix={
                      <button
                        className={css.max}
                        onClick={(e) => {
                          e.stopPropagation()
                          return process.setMax(form)
                        }}
                      >
                        Max
                      </button>
                    }
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  id="GENERATED_ID_CONVERTFORM_TSX_FORM.ITEM_VOID_2"
                  label={" "}
                  name="currencyFrom"
                >
                  <CurrencySelectField
                    disabled={process.currenciesFrom.length < 1}
                    isLoading={process.pairs.isLoading || process.isLoading}
                    onSelect={(c) => process.setCurrencyFrom(c.id)}
                    options={process.currenciesFrom}
                    selected={process.currencyFrom}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Space>

          <Space direction="vertical" style={{ width: "100%" }}>
            <Row gutter={16}>
              <Col span={16}>
                <Form.Item
                  extra={
                    process.rates &&
                    process.currencyFrom &&
                    process.currencyTo && (
                      <ConvertFormRates
                        fetchRate={process.fetchRate}
                        from={process.currencyFrom}
                        rates={process.rates}
                        to={process.currencyTo}
                      />
                    )
                  }
                  id="GENERATED_ID_CONVERTFORM_TSX_FORM.ITEM_VOID_3"
                  label="You receive"
                >
                  <Input
                    className={css.field}
                    disabled
                    placeholder="0.00"
                    value={process.rates?.amount}
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  id="GENERATED_ID_CONVERTFORM_TSX_FORM.ITEM_VOID_4"
                  label={" "}
                  name="currencyTo"
                >
                  <CurrencySelectField
                    disabled={process.currenciesTo.length < 1}
                    isLoading={process.pairs.isLoading || process.isLoading}
                    onSelect={(c) => process.setCurrencyTo(c.id)}
                    options={process.currenciesTo}
                    selected={process.currencyTo}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Space>

          {process.rateError && (
            <Alert message={process.rateError} showIcon type="error" />
          )}

          <Flex justify="center">
            <Button
              disabled={!process.rates?.token || process.isLoading}
              onClick={submit}
              size="large"
              type="primary"
            >
              Confirm
            </Button>
          </Flex>
        </Space>
      </Form>
    )
  },
)
